<div class="contract-vault-container">
  <!-- Top Navigation Bar (Cleanly ABOVE viewport, zero overlap) -->
  <div class="vault-top-bar">
    <div class="segmented-control">
      <button
        type="button"
        class="segment-btn"
        [class.active]="activeTabIndex === 0"
        (click)="activeTabIndex = 0"
      >
        <mat-icon class="btn-icon">open_in_browser</mat-icon>
        <span>Contract Workspace</span>
        <span class="live-indicator">LIVE</span>
      </button>

      <button
        type="button"
        class="segment-btn"
        [class.active]="activeTabIndex === 1"
        (click)="activeTabIndex = 1"
      >
        <mat-icon class="btn-icon">folder_zip</mat-icon>
        <span>Archived Artifacts</span>
        <span class="count-pill">{{ summary?.artifacts?.length || 0 }}</span>
      </button>
    </div>

    <div class="top-actions">
      <!-- TAB 0 ACTIONS: Live Contract Workspace -->
      <ng-container *ngIf="activeTabIndex === 0">
        <!-- Open in new tab icon -->
        <button
          *ngIf="rawEmbedUrl"
          mat-icon-button
          (click)="openInNewTab()"
          class="action-btn"
          title="Open Contract App in new window"
          matTooltip="Open Contract App in new window"
        >
          <mat-icon>open_in_new</mat-icon>
        </button>

        <!-- Reload original URL (Reset link) -->
        <button
          mat-icon-button
          (click)="reloadOriginalUrl()"
          [disabled]="summaryLoading || iframeLoading"
          class="action-btn"
          title="Reset & reload setup link"
          matTooltip="Reset & reload setup link"
        >
          <mat-icon [class.spinning]="iframeLoading">replay</mat-icon>
        </button>

        <!-- Reload current iframe page -->
        <button
          mat-icon-button
          (click)="reloadCurrentIframe()"
          [disabled]="summaryLoading || iframeLoading"
          class="action-btn"
          title="Reload current page"
          matTooltip="Reload current page"
        >
          <mat-icon [class.spinning]="iframeLoading">refresh</mat-icon>
        </button>
      </ng-container>

      <!-- TAB 1 ACTIONS: Archived Artifacts -->
      <ng-container *ngIf="activeTabIndex === 1">
        <!-- Refresh artifacts list only (NO iframe re-render) -->
        <button
          mat-icon-button
          (click)="reloadArtifactsOnly()"
          [disabled]="summaryLoading || artifactsLoading"
          class="action-btn"
          title="Refresh artifacts list"
          matTooltip="Refresh artifacts list"
        >
          <mat-icon [class.spinning]="summaryLoading || artifactsLoading">refresh</mat-icon>
        </button>
      </ng-container>

      <!-- Close dialog button -->
      <button
        mat-icon-button
        (click)="close()"
        class="action-btn close-btn"
        title="Close dialog (ESC)"
        matTooltip="Close dialog (ESC)"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Main Viewport -->
  <div class="vault-viewport">
    <!-- Error Banner -->
    <div *ngIf="errorMessage" class="error-banner">
      <mat-icon>error_outline</mat-icon>
      <span>{{ errorMessage }}</span>
      <button mat-button color="warn" (click)="loadVaultSummary(true)">Retry</button>
    </div>

    <!-- VIEW 1: Live Iframe Workspace (Keepalive with [hidden], dedicated iframe spinner) -->
    <div class="view-pane iframe-pane" [hidden]="activeTabIndex !== 0">
      <!-- State 1: Loading Overlay (During initial summary load or iframe page connect) -->
      <div *ngIf="summaryLoading || (safeEmbedUrl && iframeLoading)" class="iframe-loading-overlay">
        <div class="loading-box">
          <mat-spinner diameter="44"></mat-spinner>
          <div class="loading-text-meta">
            <h4 class="loading-title">{{ summaryLoading ? 'Initializing Contract Workspace...' : 'Connecting to Signing Session...' }}</h4>
            <p class="loading-sub">{{ summaryLoading ? 'Fetching contract execution snapshot and security tokens...' : 'Establishing live secure channel with Contract App...' }}</p>
          </div>
        </div>
      </div>

      <!-- State 2: Live Embedded Session -->
      <ng-container *ngIf="!summaryLoading && safeEmbedUrl">
        <iframe
          #contractIframe
          [src]="safeEmbedUrl"
          class="edge-to-edge-iframe"
          frameborder="0"
          allow="clipboard-write; clipboard-read"
          allowfullscreen
          (load)="onIframeLoad()"
          (error)="onIframeError()"
        ></iframe>
      </ng-container>

      <!-- State 3: Fallback / Headless Snapshot Mode (Only shown when not loading and no embed url available) -->
      <div *ngIf="!summaryLoading && !safeEmbedUrl" class="under-development-container">
        <div class="dev-card">
          <div class="icon-circle">
            <mat-icon class="dev-main-icon">layers</mat-icon>
          </div>
          <h3 class="dev-title">Interactive Contract Workspace</h3>
          <div class="badge-status">HEADLESS VAULT MODE</div>
          <p class="dev-description">
            This contract snapshot was synced and archived in Headless Mode. All executed documents, PDF contracts, and addenda are securely stored and available in the vault.
          </p>

          <div class="dev-actions-row">
            <button mat-flat-button color="primary" class="switch-tab-btn" (click)="selectTab(1)">
              <mat-icon>folder_zip</mat-icon>
              <span>View Archived Contract Files ({{ summary?.artifacts?.length || 0 }})</span>
            </button>
            <button mat-stroked-button class="reload-btn" (click)="loadVaultSummary(true)">
              <mat-icon>refresh</mat-icon>
              <span>Refresh</span>
            </button>
          </div>

          <div class="dev-footer-note">
            <mat-icon class="info-icon">lock</mat-icon>
            <span>SHA-256 Verified • Content-Addressable Storage (CAS) • Immutable Snapshot</span>
          </div>
        </div>
      </div>
    </div>


    <!-- VIEW 2: Archived Artifacts Table (Keepalive with [hidden]) -->
    <div class="view-pane artifacts-pane" [hidden]="activeTabIndex !== 1" [nbSpinner]="summaryLoading || artifactsLoading" nbSpinnerMessage="Loading artifacts...">
      <div class="artifacts-wrapper">
        <!-- Warning Banner for Failed Artifacts -->
        <div *ngIf="hasFailedArtifacts" class="failed-artifacts-banner">
          <div class="banner-content">
            <mat-icon class="banner-icon">warning</mat-icon>
            <div>
              <div class="banner-title">Document S3 Backup Notice</div>
              <div class="banner-subtitle">Some documents could not be copied to internal S3 storage. Original download links remain fully accessible.</div>
            </div>
          </div>
          <button
            mat-flat-button
            color="warn"
            class="retry-all-btn"
            (click)="retryAllFailed()"
            [disabled]="retryingAll"
          >
            <mat-icon [class.spinning]="retryingAll">refresh</mat-icon>
            <span>{{ retryingAll ? 'Retrying Backup...' : 'Retry All Failed Backups' }}</span>
          </button>
        </div>

        <div *ngIf="!summaryLoading && (!summary?.artifacts || summary.artifacts.length === 0)" class="empty-artifacts-state">
          <mat-icon class="empty-icon">description</mat-icon>
          <h4>No Contract Artifacts Archived Yet</h4>
          <p>When contracts are executed in the Contract App, signed PDF documents, riders, MSAs, and W-9 forms will automatically appear here.</p>
        </div>

        <div *ngIf="summary?.artifacts && summary.artifacts.length > 0" class="artifacts-table-container">
          <table class="artifacts-table">
            <thead>
              <tr>
                <th>Document Name</th>
                <th>Category</th>
                <th>Signatory / Party</th>
                <th>File Size</th>
                <th>Backup Status</th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let art of summary.artifacts">
                <td class="file-name-cell">
                  <div class="file-title-row">
                    <mat-icon [class]="getFileIconClass(art.fileName)">{{ getFileIcon(art.fileName) }}</mat-icon>
                    <span class="file-name-text">{{ art.fileName }}</span>
                  </div>
                </td>
                <td>
                  <span class="category-badge">{{ getArtifactTypeLabel(art.artifactType) }}</span>
                </td>
                <td>
                  <span class="party-text">{{ art.partyName || '—' }}</span>
                </td>
                <td>
                  <span class="size-text">{{ formatFileSize(art.fileSizeBytes) }}</span>
                </td>
                <td>
                  <div class="status-cell-wrapper">
                    <span
                      class="clone-badge"
                      [ngClass]="art.cloneStatus.toLowerCase()"
                      [matTooltip]="art.errorMessage || (art.cloneStatus === 'Cloned' ? 'Safely archived in internal S3' : 'Pending backup')"
                    >
                      <mat-icon *ngIf="art.cloneStatus === 'Cloned'" class="badge-icon">verified</mat-icon>
                      <mat-icon *ngIf="art.cloneStatus === 'Failed'" class="badge-icon">error_outline</mat-icon>
                      <mat-icon *ngIf="art.cloneStatus === 'Pending'" class="badge-icon">hourglass_empty</mat-icon>
                      <span>{{ art.cloneStatus === 'Cloned' ? 'Backed Up' : (art.cloneStatus === 'Failed' ? 'Backup Failed' : 'Pending') }}</span>
                    </span>
                  </div>
                </td>
                <td class="text-right action-cell">
                  <button
                    *ngIf="art.cloneStatus === 'Failed'"
                    mat-stroked-button
                    color="warn"
                    class="retry-single-btn"
                    (click)="retryClone(art)"
                    [disabled]="isRetrying(art.id)"
                    matTooltip="Retry copying this document to internal vault"
                  >
                    <mat-icon [class.spinning]="isRetrying(art.id)" class="btn-icon">refresh</mat-icon>
                    Retry
                  </button>

                  <button
                    mat-stroked-button
                    (click)="previewArtifact(art)"
                    class="preview-btn"
                    matTooltip="Preview document in full-screen modal"
                  >
                    <mat-icon class="btn-icon">visibility</mat-icon>
                    Preview
                  </button>

                  <button
                    mat-stroked-button
                    (click)="shareArtifact(art)"
                    class="share-btn"
                    matTooltip="Generate secure presigned share link"
                  >
                    <mat-icon class="btn-icon">share</mat-icon>
                    Share
                  </button>

                  <button
                    mat-stroked-button
                    color="primary"
                    (click)="downloadArtifact(art)"
                    class="download-btn"
                    matTooltip="Download document file"
                  >
                    <mat-icon class="btn-icon">file_download</mat-icon>
                    Download
                  </button>

                </td>

              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>

